import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  Send,
  Calendar,
  BarChart3,
  TrendingUp,
  Bell,
  Settings,
  Plus,
  Trash2,
  Clock,
  CheckCircle,
  AlertCircle,
  RefreshCw,
  ExternalLink,
  Building2,
  Rocket
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Brand configurations
const BRANDS = {
  cryptopulse: {
    id: 'cryptopulse',
    name: 'CryptoPulse Pro',
    color: 'from-emerald-500 to-purple-600',
    icon: '📈',
    subreddits: ['Bitcoin', 'CryptoCurrency', 'CryptoMarkets', 'BitcoinBeginners', 'CryptoTechnology'],
    templates: [
      { name: 'AI Prediction', content: '🤖 [CryptoPulse Pro] Our AI predicts Bitcoin will {direction} by {percent}% in the next {timeframe}.\n\nKey factors:\n• {factor1}\n• {factor2}\n• {factor3}\n\nWhat do you think? 👇\n\n*Not financial advice*' },
      { name: 'Market Analysis', content: '📊 Bitcoin Market Update\n\nCurrent Price: ${price}\n24h Change: {change}%\n\nOur AI sentiment analysis shows the market is {sentiment}.\n\nTrack your portfolio with CryptoPulse Pro 🚀' },
      { name: 'Feature Announcement', content: '🚀 New Feature Alert!\n\nCryptoPulse Pro now offers {feature}!\n\n✅ {benefit1}\n✅ {benefit2}\n✅ {benefit3}\n\nTry it free: cryptopulsepro.io' }
    ]
  },
  allaboardselah: {
    id: 'allaboardselah',
    name: 'AllAboardSelah',
    color: 'from-amber-500 to-orange-600',
    icon: '🛒',
    subreddits: ['ecommerce', 'Entrepreneur', 'smallbusiness', 'deals', 'shutupandtakemymoney', 'BuyItForLife'],
    templates: [
      { name: 'Product Launch', content: '🎉 New Arrival at AllAboardSelah!\n\n{product_name}\n\n✨ {feature1}\n✨ {feature2}\n✨ {feature3}\n\n🔗 Shop now: allaboardselah.com\n\nLimited stock available!' },
      { name: 'Sale Announcement', content: '🔥 SALE ALERT!\n\n{discount}% OFF everything at AllAboardSelah!\n\nUse code: {code}\n\n⏰ Ends {end_date}\n\n🛒 allaboardselah.com' },
      { name: 'Brand Story', content: '👋 Meet AllAboardSelah\n\nWe\'re a small business passionate about {mission}.\n\nWhy shop with us?\n• {reason1}\n• {reason2}\n• {reason3}\n\nThank you for supporting small business! 💚' }
    ]
  }
};

const MarketingHub = () => {
  const navigate = useNavigate();
  const [activeBrand, setActiveBrand] = useState('cryptopulse');
  const [activeTab, setActiveTab] = useState('compose');
  
  // Post composer state
  const [postTitle, setPostTitle] = useState('');
  const [postContent, setPostContent] = useState('');
  const [selectedSubreddit, setSelectedSubreddit] = useState('');
  const [scheduleDate, setScheduleDate] = useState('');
  const [isPosting, setIsPosting] = useState(false);
  
  // Analytics state
  const [sentiment, setSentiment] = useState(null);
  const [redditFeed, setRedditFeed] = useState([]);
  const [trendingTopics, setTrendingTopics] = useState([]);
  const [isLoadingAnalytics, setIsLoadingAnalytics] = useState(false);
  
  // Scheduled posts
  const [scheduledPosts, setScheduledPosts] = useState([]);
  const [postHistory, setPostHistory] = useState([]);

  const brand = BRANDS[activeBrand];

  const loadAnalytics = useCallback(async () => {
    setIsLoadingAnalytics(true);
    try {
      const [sentimentRes, feedRes, trendingRes] = await Promise.all([
        fetch(`${API_URL}/api/reddit/sentiment`),
        fetch(`${API_URL}/api/reddit/feed?limit=10`),
        fetch(`${API_URL}/api/reddit/trending`)
      ]);
      
      if (sentimentRes.ok) {
        const data = await sentimentRes.json();
        setSentiment(data);
      }
      
      if (feedRes.ok) {
        const data = await feedRes.json();
        setRedditFeed(data.posts || []);
      }
      
      if (trendingRes.ok) {
        const data = await trendingRes.json();
        setTrendingTopics(data.topics || []);
      }
    } catch (error) {
      console.error('Failed to load analytics:', error);
    } finally {
      setIsLoadingAnalytics(false);
    }
  }, []);

  useEffect(() => {
    loadAnalytics();
  }, [activeBrand, loadAnalytics]);

  // Memoize filtered lists to avoid recalculating on every render
  const filteredScheduledPosts = useMemo(() => 
    scheduledPosts.filter(p => p.brand === activeBrand), 
    [scheduledPosts, activeBrand]
  );
  
  const filteredPostHistory = useMemo(() => 
    postHistory.filter(p => p.brand === activeBrand), 
    [postHistory, activeBrand]
  );

  const applyTemplate = (template) => {
    setPostContent(template.content);
    toast.success(`Applied "${template.name}" template`);
  };

  const handlePost = async (immediate = true) => {
    if (!postTitle.trim() || !postContent.trim() || !selectedSubreddit) {
      toast.error('Please fill in all fields');
      return;
    }
    
    if (!immediate && !scheduleDate) {
      toast.error('Please select a schedule date');
      return;
    }
    
    setIsPosting(true);
    
    try {
      if (immediate) {
        const response = await fetch(`${API_URL}/api/reddit/post`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            subreddit: selectedSubreddit,
            title: postTitle,
            content: postContent
          })
        });
        
        const data = await response.json();
        
        if (data.success) {
          toast.success('Posted to Reddit!', {
            description: `View at ${data.permalink}`
          });
          setPostHistory(prev => [{
            id: Date.now(),
            brand: activeBrand,
            title: postTitle,
            subreddit: selectedSubreddit,
            timestamp: new Date().toISOString(),
            status: 'posted',
            permalink: data.permalink
          }, ...prev]);
          setPostTitle('');
          setPostContent('');
        } else {
          toast.error('Failed to post', { description: data.error });
        }
      } else {
        // Schedule post
        const newScheduled = {
          id: Date.now(),
          brand: activeBrand,
          title: postTitle,
          content: postContent,
          subreddit: selectedSubreddit,
          scheduledFor: scheduleDate,
          status: 'scheduled'
        };
        setScheduledPosts(prev => [...prev, newScheduled]);
        toast.success('Post scheduled!');
        setPostTitle('');
        setPostContent('');
        setScheduleDate('');
      }
    } catch (error) {
      toast.error('Error posting', { description: error.message });
    } finally {
      setIsPosting(false);
    }
  };

  const deleteScheduledPost = (id) => {
    setScheduledPosts(prev => prev.filter(p => p.id !== id));
    toast.success('Scheduled post deleted');
  };

  const getSentimentColor = (sentiment) => {
    switch (sentiment) {
      case 'bullish': return 'text-green-500';
      case 'slightly_bullish': return 'text-green-400';
      case 'bearish': return 'text-red-500';
      case 'slightly_bearish': return 'text-red-400';
      default: return 'text-gray-400';
    }
  };

  const getSentimentEmoji = (sentiment) => {
    switch (sentiment) {
      case 'bullish': return '🚀';
      case 'slightly_bullish': return '📈';
      case 'bearish': return '📉';
      case 'slightly_bearish': return '😟';
      default: return '😐';
    }
  };

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <header className="border-b border-white/10 sticky top-0 z-50 bg-black/90 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                onClick={() => navigate('/dashboard')}
                className="text-gray-400 hover:text-white"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Dashboard
              </Button>
              <div className="h-6 w-px bg-white/20" />
              <div className="flex items-center gap-2">
                <Rocket className="w-5 h-5 text-purple-400" />
                <h1 className="text-xl font-bold">Marketing Command Center</h1>
              </div>
            </div>
            
            {/* Brand Selector */}
            <div className="flex items-center gap-2">
              {Object.values(BRANDS).map((b) => (
                <Button
                  key={b.id}
                  data-testid={`brand-${b.id}-btn`}
                  variant={activeBrand === b.id ? 'default' : 'outline'}
                  onClick={() => setActiveBrand(b.id)}
                  className={activeBrand === b.id 
                    ? `bg-gradient-to-r ${b.color}` 
                    : 'border-white/20 text-gray-300'
                  }
                >
                  <span className="mr-2">{b.icon}</span>
                  {b.name}
                </Button>
              ))}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Bar */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card className="p-4 bg-white/5 border-white/10">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg bg-gradient-to-r ${brand.color}`}>
                <BarChart3 className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-sm text-gray-400">Market Sentiment</p>
                <p className={`text-xl font-bold ${getSentimentColor(sentiment?.sentiment)}`}>
                  {getSentimentEmoji(sentiment?.sentiment)} {sentiment?.sentiment || 'Loading...'}
                </p>
              </div>
            </div>
          </Card>
          
          <Card className="p-4 bg-white/5 border-white/10">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-500">
                <TrendingUp className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-sm text-gray-400">Sentiment Score</p>
                <p className="text-xl font-bold">{sentiment?.score || 0}/100</p>
              </div>
            </div>
          </Card>
          
          <Card className="p-4 bg-white/5 border-white/10">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-purple-500">
                <Calendar className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-sm text-gray-400">Scheduled Posts</p>
                <p className="text-xl font-bold">{scheduledPosts.filter(p => p.brand === activeBrand).length}</p>
              </div>
            </div>
          </Card>
          
          <Card className="p-4 bg-white/5 border-white/10">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-500">
                <CheckCircle className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-sm text-gray-400">Posts This Week</p>
                <p className="text-xl font-bold">{postHistory.filter(p => p.brand === activeBrand).length}</p>
              </div>
            </div>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content Area */}
          <div className="lg:col-span-2">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="bg-white/5 border border-white/10 mb-6">
                <TabsTrigger value="compose" className="data-[state=active]:bg-white/10">
                  <Send className="w-4 h-4 mr-2" />
                  Compose
                </TabsTrigger>
                <TabsTrigger value="scheduled" className="data-[state=active]:bg-white/10">
                  <Clock className="w-4 h-4 mr-2" />
                  Scheduled
                </TabsTrigger>
                <TabsTrigger value="history" className="data-[state=active]:bg-white/10">
                  <BarChart3 className="w-4 h-4 mr-2" />
                  History
                </TabsTrigger>
              </TabsList>

              <TabsContent value="compose">
                <Card className="p-6 bg-white/5 border-white/10">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <span>{brand.icon}</span>
                    Post to Reddit for {brand.name}
                  </h3>
                  
                  {/* Templates */}
                  <div className="mb-4">
                    <p className="text-sm text-gray-400 mb-2">Quick Templates:</p>
                    <div className="flex flex-wrap gap-2">
                      {brand.templates.map((template, idx) => (
                        <Button
                          key={idx}
                          variant="outline"
                          size="sm"
                          onClick={() => applyTemplate(template)}
                          className="border-white/20 text-gray-300 hover:bg-white/10"
                        >
                          {template.name}
                        </Button>
                      ))}
                    </div>
                  </div>
                  
                  {/* Subreddit Selector */}
                  <div className="mb-4">
                    <label className="text-sm text-gray-400 mb-2 block">Target Subreddit</label>
                    <Select value={selectedSubreddit} onValueChange={setSelectedSubreddit}>
                      <SelectTrigger className="bg-white/5 border-white/20">
                        <SelectValue placeholder="Select subreddit" />
                      </SelectTrigger>
                      <SelectContent>
                        {brand.subreddits.map(sub => (
                          <SelectItem key={sub} value={sub}>r/{sub}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Post Title */}
                  <div className="mb-4">
                    <label className="text-sm text-gray-400 mb-2 block">Post Title</label>
                    <Input
                      value={postTitle}
                      onChange={(e) => setPostTitle(e.target.value)}
                      placeholder="Enter an engaging title..."
                      className="bg-white/5 border-white/20"
                    />
                  </div>
                  
                  {/* Post Content */}
                  <div className="mb-4">
                    <label className="text-sm text-gray-400 mb-2 block">Content</label>
                    <Textarea
                      value={postContent}
                      onChange={(e) => setPostContent(e.target.value)}
                      placeholder="Write your post content..."
                      rows={8}
                      className="bg-white/5 border-white/20"
                    />
                  </div>
                  
                  {/* Schedule Option */}
                  <div className="mb-6">
                    <label className="text-sm text-gray-400 mb-2 block">Schedule (optional)</label>
                    <Input
                      type="datetime-local"
                      value={scheduleDate}
                      onChange={(e) => setScheduleDate(e.target.value)}
                      className="bg-white/5 border-white/20"
                    />
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="flex gap-3">
                    <Button
                      onClick={() => handlePost(true)}
                      disabled={isPosting}
                      className={`flex-1 bg-gradient-to-r ${brand.color}`}
                    >
                      {isPosting ? (
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Send className="w-4 h-4 mr-2" />
                      )}
                      Post Now
                    </Button>
                    <Button
                      onClick={() => handlePost(false)}
                      disabled={isPosting || !scheduleDate}
                      variant="outline"
                      className="border-white/20"
                    >
                      <Clock className="w-4 h-4 mr-2" />
                      Schedule
                    </Button>
                  </div>
                </Card>
              </TabsContent>

              <TabsContent value="scheduled">
                <Card className="p-6 bg-white/5 border-white/10">
                  <h3 className="text-lg font-semibold mb-4">Scheduled Posts</h3>
                  {filteredScheduledPosts.length === 0 ? (
                    <p className="text-gray-400 text-center py-8">No scheduled posts</p>
                  ) : (
                    <div className="space-y-4">
                      {filteredScheduledPosts.map(post => (
                        <div key={post.id} className="p-4 bg-white/5 rounded-lg flex items-start justify-between">
                          <div>
                            <p className="font-medium">{post.title}</p>
                            <p className="text-sm text-gray-400">r/{post.subreddit}</p>
                            <p className="text-sm text-purple-400 flex items-center gap-1 mt-1">
                              <Clock className="w-3 h-3" />
                              {new Date(post.scheduledFor).toLocaleString()}
                            </p>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => deleteScheduledPost(post.id)}
                            className="text-red-400 hover:text-red-300"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </Card>
              </TabsContent>

              <TabsContent value="history">
                <Card className="p-6 bg-white/5 border-white/10">
                  <h3 className="text-lg font-semibold mb-4">Post History</h3>
                  {filteredPostHistory.length === 0 ? (
                    <p className="text-gray-400 text-center py-8">No posts yet</p>
                  ) : (
                    <div className="space-y-4">
                      {filteredPostHistory.map(post => (
                        <div key={post.id} className="p-4 bg-white/5 rounded-lg">
                          <div className="flex items-start justify-between">
                            <div>
                              <p className="font-medium">{post.title}</p>
                              <p className="text-sm text-gray-400">r/{post.subreddit}</p>
                              <p className="text-sm text-gray-500 mt-1">
                                {new Date(post.timestamp).toLocaleString()}
                              </p>
                            </div>
                            {post.permalink && (
                              <a
                                href={post.permalink}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-purple-400 hover:text-purple-300"
                              >
                                <ExternalLink className="w-4 h-4" />
                              </a>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </Card>
              </TabsContent>
            </Tabs>
          </div>

          {/* Sidebar - Analytics */}
          <div className="space-y-6">
            {/* Trending Topics */}
            <Card className="p-6 bg-white/5 border-white/10">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-purple-400" />
                  Trending Topics
                </h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={loadAnalytics}
                  disabled={isLoadingAnalytics}
                >
                  <RefreshCw className={`w-4 h-4 ${isLoadingAnalytics ? 'animate-spin' : ''}`} />
                </Button>
              </div>
              {trendingTopics.length === 0 ? (
                <p className="text-gray-400 text-sm">No trending topics</p>
              ) : (
                <div className="space-y-2">
                  {trendingTopics.slice(0, 8).map((topic) => (
                    <div key={topic.topic} className="flex items-center justify-between text-sm">
                      <span className="text-gray-300">{topic.topic}</span>
                      <span className="text-gray-500">{topic.score}</span>
                    </div>
                  ))}
                </div>
              )}
            </Card>

            {/* Reddit Feed */}
            <Card className="p-6 bg-white/5 border-white/10">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <Bell className="w-4 h-4 text-orange-400" />
                Reddit Feed
              </h3>
              {redditFeed.length === 0 ? (
                <p className="text-gray-400 text-sm">No posts loaded</p>
              ) : (
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {redditFeed.slice(0, 10).map((post) => (
                    <a
                      key={post.id || post.permalink}
                      href={post.permalink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block p-3 bg-white/5 rounded-lg hover:bg-white/10 transition-colors"
                    >
                      <p className="text-sm font-medium line-clamp-2">{post.title}</p>
                      <div className="flex items-center gap-3 mt-2 text-xs text-gray-400">
                        <span>r/{post.subreddit}</span>
                        <span>⬆ {post.score}</span>
                        <span>💬 {post.num_comments}</span>
                      </div>
                    </a>
                  ))}
                </div>
              )}
            </Card>

            {/* Quick Actions */}
            <Card className="p-6 bg-white/5 border-white/10">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <Rocket className="w-4 h-4 text-green-400" />
                Quick Actions
              </h3>
              <div className="space-y-2">
                <Button
                  variant="outline"
                  className="w-full justify-start border-white/20 text-gray-300"
                  onClick={() => window.open('https://reddit.com/submit', '_blank')}
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Open Reddit
                </Button>
                <Button
                  variant="outline"
                  className="w-full justify-start border-white/20 text-gray-300"
                  onClick={() => {
                    setActiveBrand(activeBrand === 'cryptopulse' ? 'allaboardselah' : 'cryptopulse');
                  }}
                >
                  <Building2 className="w-4 h-4 mr-2" />
                  Switch Brand
                </Button>
              </div>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
};

export default MarketingHub;
